import { Radio } from '@thanx/uikit/radio'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

type Props = {
  selected: boolean
  value: string
  name: string
  onChange: () => void
  children: React.ReactNode
  // Optional gated/disabled affordance. Defaults to false so existing callers
  // are unaffected. When true the card swaps to a muted grey treatment, drops
  // the hover/transition, and disables the inner Radio.
  disabled?: boolean
  // Accessible name for the inner Radio (already-translated option title).
  // REQUIRED: the card wrapper is a plain <div> — not a <label> — because
  // children can contain other interactive controls (e.g. the custom-text
  // textarea) and a label may wrap at most one control (WCAG 1.3.1). Without
  // this the radio has no programmatic name at all.
  ariaLabel: string
  // Optional id of an element describing the option (WCAG 1.3.1). Since the
  // radio's name comes from `ariaLabel`, supplementary helper copy in `children`
  // is otherwise invisible to AT; threading its id here as aria-describedby
  // makes it part of the radio's accessible description.
  ariaDescribedBy?: string
}

// Stable no-op for the disabled state. An inline `() => {}` would be a new
// function reference on every render — a "changed" prop to any memoized child
// and a per-render allocation for nothing.
const noop = () => {}

const RadioOptionCard = ({
  selected,
  value,
  name,
  onChange,
  children,
  disabled = false,
  ariaLabel,
  ariaDescribedBy,
}: Props) => {
  const [css] = useStyletron()

  const baseStyle = {
    borderRadius: '4px',
    borderStyle: 'solid',
    borderWidth: '1px',
  }

  const disabledStyle = {
    ...baseStyle,
    backgroundColor: merchantTheme.colors.grey05,
    borderColor: merchantTheme.colors.grey50,
    cursor: 'not-allowed',
  }

  const interactiveStyle = {
    ...baseStyle,
    transition: 'border-color 300ms ease-out',
    backgroundColor: 'white',
    borderColor: selected
      ? merchantTheme.colors.belizeHole50
      : merchantTheme.colors.grey50,
    ':hover': {
      borderColor: merchantTheme.colors.belizeHole50,
    },
  }

  return (
    // A <div> (not a <label>): children may contain interactive controls
    // (textarea, inputs), and a label wrapping two controls violates WCAG
    // 1.3.1. Click-anywhere-to-select is preserved via onClick; the inner
    // Radio remains the keyboard/semantic control, so the div deliberately
    // carries no role/tabIndex (nesting interactive roles would be worse).
    // The Radio's own `disabled` prop is the assistive-tech signal — no
    // aria-disabled needed here.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={disabled ? undefined : onChange}
      className={`w-100 d-flex p-m align-items-start ${css(
        disabled ? disabledStyle : interactiveStyle
      )}`}
    >
      <div className="mt-n2">
        <Radio
          value={value}
          name={name}
          checked={disabled ? false : selected}
          disabled={disabled}
          onChange={disabled ? noop : onChange}
          // The card title lives outside the Radio's own label, so thread the
          // accessible name (and any description id) down to the native input
          // (baseui Input override).
          overrides={{
            Input: {
              props: {
                'aria-label': ariaLabel,
                ...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy }),
              },
            },
          }}
        />
      </div>
      <div className="w-100 pl-xs d-flex flex-column">{children}</div>
    </div>
  )
}

export default RadioOptionCard
